import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Star, CheckCircle2, XCircle, LogOut, Lock, Loader2, RefreshCw, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "click-admin-token";

const Stars = ({ value }) => (
  <div className="flex gap-1">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        size={14}
        className={i < value ? "fill-current text-gold" : "text-border-soft"}
      />
    ))}
  </div>
);

const Badge = ({ approved }) => (
  <span
    className={`inline-block text-[10px] tracking-[0.2em] uppercase font-medium px-2 py-1 ${
      approved
        ? "bg-emerald-100 text-emerald-800"
        : "bg-amber-100 text-amber-800"
    }`}
  >
    {approved ? "Published" : "Pending"}
  </span>
);

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Europe/Athens",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }) + " Athens";
  } catch {
    return iso;
  }
};

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErr("");
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      if (r.data?.token) {
        localStorage.setItem(TOKEN_KEY, r.data.token);
        onLogin(r.data.token);
      }
    } catch (er) {
      setStatus("idle");
      setErr(er?.response?.status === 401 ? "Wrong password." : "Login failed. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white border border-border-soft p-8 md:p-10"
      >
        <div className="w-11 h-11 rounded-full bg-bg-alt flex items-center justify-center text-gold mx-auto">
          <Lock size={18} />
        </div>
        <p className="overline text-center mt-6">Admin access</p>
        <h1 className="font-serif text-2xl md:text-3xl text-accent-dark text-center mt-2 font-light leading-tight">
          Click <span className="italic text-gold">Photography</span>
        </h1>
        <p className="text-sm text-text-muted text-center mt-3">
          Enter the admin password to moderate reviews and view bookings.
        </p>

        <label className="block mt-8 text-[11px] tracking-[0.2em] uppercase text-text-muted font-medium mb-2">
          Password
        </label>
        <input
          type="password"
          data-testid="admin-password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-white border border-border-soft px-4 py-3 text-sm text-accent-dark font-sans focus:outline-none focus:border-gold transition-colors"
        />
        {err && (
          <p data-testid="admin-login-error" className="mt-3 text-sm text-red-600">
            {err}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={status === "sending"}
          className="btn-luxury w-full mt-6 disabled:opacity-60"
          style={{ padding: "0.9rem 1.5rem", fontSize: "0.7rem" }}
        >
          {status === "sending" ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </button>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-text-muted hover:text-accent-dark transition-colors"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("pending"); // pending | published | bookings
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // id being processed

  const authed = (config = {}) => ({
    ...config,
    headers: { ...(config.headers || {}), "X-Admin-Password": token },
  });

  const loadReviews = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, authed());
      setReviews(Array.isArray(r.data) ? r.data : []);
    } catch (er) {
      if (er?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadBookings = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/bookings`, authed());
      setBookings(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      loadReviews();
      loadBookings();
    }
  }, [token, loadReviews, loadBookings]);

  if (!token) return <LoginForm onLogin={setToken} />;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, null, authed());
      await loadReviews();
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    setBusy(id);
    try {
      await axios.post(`${API}/admin/reviews/${id}/reject`, null, authed());
      await loadReviews();
    } finally {
      setBusy(null);
    }
  };

  const pending = reviews.filter((r) => !r.approved);
  const published = reviews.filter((r) => r.approved);

  return (
    <div className="min-h-screen bg-bg-base font-sans">
      <header className="bg-white border-b border-border-soft sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div>
            <Link
              to="/"
              className="font-serif text-xl md:text-2xl tracking-tight text-accent-dark"
            >
              Click <span className="italic text-gold">Photography</span>
            </Link>
            <p className="text-[10px] tracking-[0.25em] uppercase text-text-muted mt-0.5">
              Admin panel
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                loadReviews();
                loadBookings();
              }}
              aria-label="Refresh"
              className="p-2 text-text-muted hover:text-accent-dark transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-medium text-text-muted hover:text-accent-dark transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border-soft mb-8">
          {[
            { id: "pending", label: `Pending reviews (${pending.length})` },
            { id: "published", label: `Published (${published.length})` },
            { id: "bookings", label: `Bookings (${bookings.length})` },
          ].map((tabItem) => (
            <button
              key={tabItem.id}
              data-testid={`admin-tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`px-4 py-3 text-[11px] tracking-[0.2em] uppercase font-medium transition-colors border-b-2 -mb-px ${
                tab === tabItem.id
                  ? "border-gold text-accent-dark"
                  : "border-transparent text-text-muted hover:text-accent-dark"
              }`}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {/* Reviews tab */}
        {tab !== "bookings" && (
          <div className="space-y-5">
            {(tab === "pending" ? pending : published).length === 0 && (
              <div className="bg-white border border-border-soft p-10 text-center text-text-muted">
                <p className="font-serif text-2xl text-accent-dark font-light">
                  Nothing here yet.
                </p>
                <p className="mt-2 text-sm">
                  {tab === "pending"
                    ? "You'll see new client reviews here as they come in."
                    : "No reviews have been approved yet."}
                </p>
              </div>
            )}
            {(tab === "pending" ? pending : published).map((r) => (
              <article
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="bg-white border border-border-soft p-6 md:p-8"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <Stars value={r.rating} />
                      <Badge approved={r.approved} />
                    </div>
                    <p className="font-serif text-xl md:text-2xl text-accent-dark mt-3 font-light">
                      {r.name}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      {[r.session_type, r.location].filter(Boolean).join(" · ")}
                      {r.session_type || r.location ? " · " : ""}
                      {formatDate(r.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 shrink-0">
                    {!r.approved && (
                      <button
                        data-testid={`approve-${r.id}`}
                        disabled={busy === r.id}
                        onClick={() => approve(r.id)}
                        className="inline-flex items-center justify-center gap-1.5 bg-gold text-white text-[11px] tracking-[0.15em] uppercase font-medium px-4 py-2.5 hover:bg-accent-dark transition-colors disabled:opacity-50"
                      >
                        {busy === r.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <CheckCircle2 size={12} />
                        )}
                        Approve
                      </button>
                    )}
                    <button
                      data-testid={`reject-${r.id}`}
                      disabled={busy === r.id}
                      onClick={() => reject(r.id)}
                      className="inline-flex items-center justify-center gap-1.5 bg-white text-accent-dark border border-border-soft text-[11px] tracking-[0.15em] uppercase font-medium px-4 py-2.5 hover:border-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      <XCircle size={12} />
                      {r.approved ? "Delete" : "Reject"}
                    </button>
                  </div>
                </div>

                <blockquote className="mt-5 font-serif text-lg md:text-xl text-accent-dark leading-snug font-light border-l-2 border-gold pl-5">
                  “{r.message}”
                </blockquote>
              </article>
            ))}
          </div>
        )}

        {/* Bookings tab */}
        {tab === "bookings" && (
          <div className="space-y-4">
            {bookings.length === 0 && (
              <div className="bg-white border border-border-soft p-10 text-center text-text-muted">
                <p className="font-serif text-2xl text-accent-dark font-light">
                  No bookings yet.
                </p>
              </div>
            )}
            {bookings.map((b) => (
              <article
                key={b.id}
                data-testid={`admin-booking-${b.id}`}
                className="bg-white border border-border-soft p-6 md:p-8"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <p className="font-serif text-xl md:text-2xl text-accent-dark font-light">
                      {b.name}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      {b.session_type} · {formatDate(b.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <a
                      href={`mailto:${b.email}`}
                      className="text-gold hover:underline"
                    >
                      {b.email}
                    </a>
                    <a
                      href={`tel:${b.phone}`}
                      className="text-text-muted hover:text-accent-dark"
                    >
                      {b.phone}
                    </a>
                  </div>
                </div>
                <dl className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {b.preferred_date && (
                    <div>
                      <dt className="text-[10px] tracking-[0.2em] uppercase text-text-muted">Date</dt>
                      <dd className="mt-1">{b.preferred_date}</dd>
                    </div>
                  )}
                  {b.people && (
                    <div>
                      <dt className="text-[10px] tracking-[0.2em] uppercase text-text-muted">People</dt>
                      <dd className="mt-1">{b.people}</dd>
                    </div>
                  )}
                  {b.location && (
                    <div className="col-span-2">
                      <dt className="text-[10px] tracking-[0.2em] uppercase text-text-muted">Location</dt>
                      <dd className="mt-1">{b.location}</dd>
                    </div>
                  )}
                </dl>
                {b.message && (
                  <p className="mt-5 text-sm text-text-muted leading-relaxed border-l-2 border-border-soft pl-4">
                    {b.message}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
